import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  canLockIncident,
  fetchIncidentList,
  INCIDENT_EVIDENCE_BYTES_CONFIRMED,
  INCIDENT_EVIDENCE_BYTES_UNCONFIRMED,
  INCIDENT_EVIDENCE_CHECKSUM_ABSENT,
  INCIDENT_EVIDENCE_CHECKSUM_PRESENT,
  INCIDENT_LOCK_FINALITY,
  canStartIncidentReview,
  fetchIncidentAttachments,
  lockIncident,
  reviewStateLabel,
  SAFE_INCIDENT_WRITE_FAILURE,
  startIncidentReview,
  type IncidentAttachment,
  type IncidentRow,
} from "../data/incidents";
import { fetchIncidentCallRef } from "../data/duty-call-incidents";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

// Incident detail desk (D-01 design, D-03 sequencing, D-07 opaque ids).
//
// Built on what main already ships: public.management_field_incident_list for the record,
// the evidence attachment list for media metadata, and the existing review_field_incident /
// lock_field_incident RPCs. No new read model and no new write path.
//
// Three things the design mock shows that this surface deliberately does NOT:
//
//   * A record REFERENCE. The mock's own implement notes say a numbering or reference scheme
//     was "deliberately not invented here", and none exists on main. So the detail shows no
//     identifier at all rather than inventing INC-YYYY or dressing a truncated UUID as a
//     reference operators could quote. D-07 is satisfied by showing nothing.
//   * The SOS event stream, recorded positions, and derived response metrics. Those need an
//     incident record that joins the SOS log, which does not exist. What is shown here is the
//     FIELD incident record that does.
//   * A "close" action. Main has review and lock, and lock is the terminal filing state. This
//     surface uses those words rather than borrowing the mock's, because a button labeled
//     Close that actually locks would teach the wrong thing about an irreversible step.
//
// Review and lock reuse the data layer's own guards, so this surface and the incident feed
// cannot disagree about what is permitted.

export const INCIDENT_DETAIL_UNAVAILABLE =
  "This incident is not in the list this desk can read. The list is capped at the most recent records, so an older incident can fall outside it. Your access may also have changed.";

export function IncidentDetail({
  client,
  accountId,
  incident,
  timeZone,
  canManage = false,
  onChanged,
}: {
  client: SupabaseClient;
  accountId: string;
  /** Null when the selected incident is no longer in the list this desk can read. */
  incident: IncidentRow | null;
  timeZone: string;
  /** evidence.manage. The server re-checks; this only shapes what is offered. */
  canManage?: boolean;
  onChanged: () => void;
}) {
  const [attachments, setAttachments] = useState<IncidentAttachment[] | "loading" | "error">(
    "loading",
  );
  // Detail-scoped: the list view carries `duty_call_id` but not `call_ref`. One selected record
  // needs one Call lookup. `null` covers both a standalone report and a link whose Call this desk
  // cannot read, so the chip only claims a ref it read.
  //
  // The link read goes through `management_field_incident_list`, so `evidence.view` already ran.
  // `incidentId` is null unless that view produced the row.
  const [callRef, setCallRef] = useState<string | null>(null);
  const [busy, setBusy] = useState<"review" | "lock" | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmLock, setConfirmLock] = useState(false);

  const incidentId = incident?.incidentId ?? null;
  useEffect(() => {
    if (incidentId === null) {
      setCallRef(null);
      return;
    }
    let active = true;
    setCallRef(null);
    fetchIncidentCallRef(client, accountId, incidentId)
      .then((ref) => {
        if (active) setCallRef(ref);
      })
      .catch(() => {
        // A quiet provenance chip is not worth an error lane. Absent means "not shown", and the
        // record itself never depended on it.
        if (active) setCallRef(null);
      });
    return () => {
      active = false;
    };
  }, [client, accountId, incidentId]);
  useEffect(() => {
    if (incidentId === null) return;
    let active = true;
    setAttachments("loading");
    fetchIncidentAttachments(client, accountId, incidentId)
      .then((rows) => {
        if (active) setAttachments(rows);
      })
      .catch(() => {
        if (active) setAttachments("error");
      });
    return () => {
      active = false;
    };
  }, [client, accountId, incidentId]);

  if (incident === null) {
    return (
      <section className="incdetail" aria-label="Incident detail">
        <p className="surface__state" role="status">
          {INCIDENT_DETAIL_UNAVAILABLE}
        </p>
      </section>
    );
  }

  async function run(kind: "review" | "lock"): Promise<void> {
    if (busy !== null || incident === null) return;
    // Structural, not positional. The actions block below already renders behind canManage and
    // behind the shipped predicates, but the write itself must not depend on where a button
    // happened to be mounted: re-check both here so a locked or ineligible record can never be
    // written from this surface even if the confirm state outlived the row it was opened on.
    // The server re-checks all of it regardless; this is the client refusing to ask.
    if (!canManage) return;
    if (kind === "lock" ? !canLockIncident(incident) : !canStartIncidentReview(incident)) return;
    setBusy(kind);
    setFailure(null);
    try {
      const result =
        kind === "lock"
          ? await lockIncident(client, accountId, incident.incidentId)
          : await startIncidentReview(client, accountId, incident.incidentId);
      if (result.ok) {
        setConfirmLock(false);
        onChanged();
        return;
      }
      setFailure(result.reason);
    } catch {
      setFailure(SAFE_INCIDENT_WRITE_FAILURE);
    } finally {
      setBusy(null);
    }
  }

  const state = reviewStateLabel(incident);
  const time = (iso: string | null) => (iso === null ? null : formatDisplayTime(iso, timeZone));

  return (
    <section className="incdetail" aria-label={`Incident detail - ${incident.title}`}>
      <div className="incdetail__head">
        <h2 className="incdetail__title">{incident.title}</h2>
        <span className={`incdetail__state incdetail__state--${state === "Locked" ? "locked" : "open"}`}>
          {state}
        </span>
      </div>

      {/* Provenance only. A standalone More report has no chip, and the Call id never shows. */}
      {callRef !== null ? (
        <p className="incdetail__from-call">From Call {callRef}</p>
      ) : null}

      <dl className="incdetail__facts">
        <Fact label="Status" value={humanizeLabel(incident.status)} />
        {incident.category !== null ? (
          <Fact label="Category" value={humanizeLabel(incident.category)} />
        ) : null}
        {incident.severity !== null ? (
          <Fact label="Severity" value={humanizeLabel(incident.severity)} />
        ) : null}
        <Fact label="Site" value={incident.siteName} />
        <Fact label="Route" value={incident.routeName} />
        <Fact label="Checkpoint" value={incident.checkpointName} />
        <Fact label="Reported by" value={incident.reportedByLabel} />
        <Fact label="Occurred" value={time(incident.occurredAt)} />
        <Fact label="Submitted" value={time(incident.submittedAt)} />
        <Fact label="Reviewed by" value={incident.reviewedByLabel} />
        <Fact label="Reviewed" value={time(incident.reviewedAt)} />
        <Fact label="Locked" value={time(incident.lockedAt)} />
      </dl>

      <p className="incdetail__notes">
        {incident.notesPresent
          ? "The filer wrote notes on this incident. Notes are not shown here; open the record in the field evidence export when that ships."
          : "The filer wrote no notes on this incident."}
      </p>

      <IncidentMedia attachments={attachments} incident={incident} timeZone={timeZone} />

      {canManage ? (
        <div className="incdetail__actions">
          {canStartIncidentReview(incident) ? (
            <button
              type="button"
              className="builder__navbtn"
              disabled={busy !== null}
              onClick={() => void run("review")}
            >
              Start review
            </button>
          ) : null}
          {canLockIncident(incident) ? (
            confirmLock ? (
              <>
                <p className="incdetail__confirm" role="status">
                  {INCIDENT_LOCK_FINALITY}
                </p>
                <button
                  type="button"
                  className="minter__confirm-go"
                  disabled={busy !== null}
                  onClick={() => void run("lock")}
                >
                  Lock the record
                </button>
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={busy !== null}
                  onClick={() => setConfirmLock(false)}
                >
                  Keep it open
                </button>
              </>
            ) : (
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy !== null}
                onClick={() => setConfirmLock(true)}
              >
                Lock…
              </button>
            )
          ) : null}
          {!canStartIncidentReview(incident) && !canLockIncident(incident) ? (
            <p className="incdetail__quiet">
              {state === "Locked"
                ? "This record is locked. Nothing further can be changed here."
                : "No review action is available for this incident right now."}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="incdetail__quiet">
          Reviewing and locking an incident needs evidence manage permission.
        </p>
      )}

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="incdetail__fact">
      <dt>{label}</dt>
      <dd>{value ?? "Not recorded"}</dd>
    </div>
  );
}

/**
 * Media honesty: this lists what the evidence read model says EXISTS, with the counts the
 * record itself carries. It never renders the media and never implies a file is retrievable,
 * because opening one is a separate audited step that lives on the incident feed.
 */
function IncidentMedia({
  attachments,
  incident,
  timeZone,
}: {
  attachments: IncidentAttachment[] | "loading" | "error";
  incident: IncidentRow;
  timeZone: string;
}) {
  if (attachments === "loading") {
    return (
      <p className="surface__state" role="status">
        Loading evidence metadata...
      </p>
    );
  }
  if (attachments === "error") {
    // Distinct from "no attachments": one of those means nothing was filed.
    return (
      <p className="surface__state surface__state--error" role="alert">
        Could not load the evidence list for this incident. The record above still stands;
        treat the attachment count as unconfirmed.
      </p>
    );
  }
  const mismatch = attachments.length !== incident.attachmentCount;
  if (attachments.length === 0) {
    return (
      <p className="incdetail__quiet">
        No evidence is attached to this incident.
        {mismatch ? " The record counts attachments, so this list is out of step with it." : ""}
      </p>
    );
  }
  return (
    <div className="incdetail__media">
      {/* One number, from one source: the list actually read. Mixing it with the record's
          photo count in the same parenthesis invited the two to be read as one total. */}
      <h3 className="incdetail__subtitle">Evidence ({attachments.length} attached)</h3>
      {mismatch ? (
        <p className="incdetail__quiet">
          The record counts {incident.attachmentCount}, so this list is out of step with it.
        </p>
      ) : null}
      <ul className="incdetail__attachments" aria-label="Evidence attachments">
        {attachments.map((item) => (
          <li key={item.attachmentId} className="incdetail__attachment">
            <span className="incdetail__attachment-name">
              {item.displayName ?? humanizeLabel(item.kind ?? "attachment")}
            </span>
            <span className="incdetail__attachment-meta">
              {item.capturedAt !== null ? formatDisplayTime(item.capturedAt, timeZone) : "time not recorded"}
              {` · ${item.storageAttached ? INCIDENT_EVIDENCE_BYTES_CONFIRMED : INCIDENT_EVIDENCE_BYTES_UNCONFIRMED}`}
              {` · ${item.sha256Present ? INCIDENT_EVIDENCE_CHECKSUM_PRESENT : INCIDENT_EVIDENCE_CHECKSUM_ABSENT}`}
            </span>
          </li>
        ))}
      </ul>
      <p className="incdetail__quiet">
        Metadata only. Opening a file is a separate audited step on the incident list.
      </p>
    </div>
  );
}


export const INCIDENT_DETAIL_LOAD_FAILED =
  "Could not load that incident. Check your connection and try again.";

/** The list read carries the account it was read FOR, so rows can never outlive that account. */
interface AccountScopedIncidents {
  accountId: string;
  rows: readonly IncidentRow[];
}

/**
 * Resolves the incident by id from the list read this account already uses. There is no
 * detail read model on main, and holding the row the list handed over would let the desk keep
 * showing a record after a review or lock changed it. Re-resolving means the detail is always
 * the list's answer, and a row that has left the list resolves to the unavailable state.
 */
export function IncidentDetailPanel({
  client,
  accountId,
  incidentId,
  timeZone,
  canManage = false,
}: {
  client: SupabaseClient;
  accountId: string;
  incidentId: string;
  timeZone: string;
  canManage?: boolean;
}) {
  const { status, data, reload } = useReadModel<AccountScopedIncidents>(
    async () => {
      const read = await fetchIncidentList(client, accountId);
      if (read.status === "unavailable") {
        throw new Error(INCIDENT_DETAIL_LOAD_FAILED);
      }
      return {
        accountId,
        rows: read.status === "ok" ? read.rows : [],
      };
    },
    [client, accountId],
  );

  // The shell rewrites accountId under this panel WITHOUT remounting it - a workspace switch,
  // or a support (act-as) session starting or ending. useReadModel deliberately keeps the last
  // successful result on screen while the next read is in flight, and never clears it if that
  // read fails, so `data` alone would let this desk render one account's incident record under
  // another account's session, indefinitely on a failed read. The account the rows were read
  // for is the only thing worth trusting: anything else is not this account's answer yet.
  const rows = data !== null && data.accountId === accountId ? data.rows : null;

  if (rows === null) {
    if (status === "error") {
      return (
        <p className="surface__state surface__state--error" role="alert">
          {INCIDENT_DETAIL_LOAD_FAILED}
        </p>
      );
    }
    return (
      <p className="surface__state" role="status">
        Loading incident...
      </p>
    );
  }
  return (
    <IncidentDetail
      client={client}
      accountId={accountId}
      incident={rows.find((row) => row.incidentId === incidentId) ?? null}
      timeZone={timeZone}
      canManage={canManage}
      onChanged={reload}
    />
  );
}
